import { TaskTypeViewSchema, type TaskTypeView } from '@erp/shared-types';

export type ViewDraftResult =
  | { ok: true; view: TaskTypeView | null }
  | { ok: false; code: 'invalid_json' | 'invalid_view'; detail: string };

/** Blank clears the view. Anything else must be the shared view schema. */
export function parseViewDraft(text: string): ViewDraftResult {
  const trimmed = text.trim();
  if (trimmed === '') return { ok: true, view: null };
  const json = parseJson(trimmed);
  if (!json.ok) return json;
  const parsed = TaskTypeViewSchema.safeParse(json.value);
  if (!parsed.success) {
    return {
      ok: false,
      code: 'invalid_view',
      detail: parsed.error.issues[0]?.message ?? '',
    };
  }
  return { ok: true, view: parsed.data };
}

export function viewDraftErrorKind(
  parsed: Extract<ViewDraftResult, { ok: false }>,
): 'invalid_json' | 'duplicate_ids' | 'invalid_view' {
  if (parsed.code === 'invalid_json') return 'invalid_json';
  if (parsed.detail === 'View ids must be unique') return 'duplicate_ids';
  return 'invalid_view';
}

export function viewTextFromStored(view: unknown): string {
  if (view == null) return '';
  return JSON.stringify(view, null, 2);
}

export function viewForSave(text: string): TaskTypeView | null {
  const parsed = parseViewDraft(text);
  if (!parsed.ok) {
    throw new Error('Task type view JSON is invalid');
  }
  return parsed.view;
}

function parseJson(
  text: string,
):
  | { ok: true; value: unknown }
  | { ok: false; code: 'invalid_json'; detail: string } {
  try {
    const value: unknown = JSON.parse(text);
    return { ok: true, value };
  } catch {
    return { ok: false, code: 'invalid_json', detail: '' };
  }
}
